import React, { useEffect } from "react";
import { Sparkles, ChevronRight } from "lucide-react";

interface GetStartedMessageProps {
  handleGetStarted: () => void;
  handleSkip?: () => void;
  disabled?: boolean;
  onContentLoaded?: () => void;
  /** When true, hides the gradient icon + "It's great getting to know you!" header row. */
  hideHeading?: boolean;
}

const GetStartedMessage: React.FC<GetStartedMessageProps> = ({
  handleGetStarted,
  handleSkip,
  disabled,
  onContentLoaded,
  hideHeading = false,
}) => {
  useEffect(() => {
    onContentLoaded?.();
  }, [onContentLoaded]);

  return (
    <div className="bg-white dark:bg-surface-form-card rounded-2xl border border-secondary-200 dark:border-surface-form-border shadow-card p-10 flex flex-col gap-8">
      {/* Header: gradient icon + title */}
      {!hideHeading && (
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 flex-shrink-0 rounded-2xl bg-primary-gradient flex items-center justify-center shadow-[0px_10px_15px_0px_rgba(147,51,234,0.3),0px_4px_6px_0px_rgba(147,51,234,0.3)]">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-secondary-900 dark:text-white leading-tight">
            It's great getting to know you!
          </h1>
        </div>
      )}

      {/* Body content */}
      <div className="flex flex-col gap-6">
        <p className="text-lg text-accent-250 dark:text-pab-dark-subtle-text">
          Let's write some goals for the next few months.
        </p>

        {/* SMART / OKR info box */}
        <div className="bg-secondary-50 dark:bg-white/5 border-l-4 border-primary-purple pl-7 pr-6 py-6 rounded-r-xl flex flex-col gap-4">
          <p className="text-secondary-900 dark:text-white text-base leading-relaxed">
            We use the <span className="font-semibold text-primary-purple">SMART method</span>
            <span className="text-accent-250 dark:text-pab-dark-subtle-text text-sm">
              {" "}
              (Specific, Measurable, Achievable, Relevant, Time-bound){" "}
            </span>
            to communicate your <span className="font-semibold text-primary-purple">OKRs</span>
            <span className="text-accent-250 dark:text-pab-dark-subtle-text text-sm">
              {" "}
              (Objectives &amp; Key Results){" "}
            </span>
            with your Advisors.
          </p>
          <p className="text-accent-250 dark:text-pab-dark-subtle-text text-base leading-relaxed">
            These goals will guide your action plan and shape your{" "}
            <span className="font-semibold text-secondary-900 dark:text-white">STAR portfolio</span>
            <span className="text-accent-250 dark:text-pab-dark-subtle-text text-sm">
              {" "}
              (Situation, Task, Action, Result){" "}
            </span>
            used during reviews and advancement conversations.
          </p>
        </div>

        {/* AI recommendations box */}
        <div className="bg-accent-350 dark:bg-pab-dark-session-bg border border-accent-150 dark:border-pab-dark-session-border rounded-xl px-5 py-5">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-primary-750 dark:text-pab-dark-session-title flex-shrink-0 mt-0.5" />
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-sm text-primary-750 dark:text-pab-dark-session-title">
                AI-Powered Recommendations
              </p>
              <p className="text-sm text-primary-600 dark:text-pab-dark-subtle-text leading-5">
                Based on your profile, company function, and growth areas, we've generated
                personalized focus areas across leadership, execution, and impact.
              </p>
            </div>
          </div>
        </div>

        {/* CTA buttons */}
        <div className="flex flex-col gap-3">
          <button onClick={handleGetStarted} disabled={disabled} className="btn-gradient-cta">
            Let's Get Started
            <ChevronRight className="w-5 h-5" />
          </button>
          {handleSkip && (
            <button onClick={handleSkip} disabled={disabled} className="btn-gradient-cta">
              Skip for now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GetStartedMessage;
